import { Component, inject } from '@angular/core';
import { Router } from '@angular/router';
import { DataStoreService } from '../../services/data-store';
import { CsvParserService } from '../../services/csv-parser';
import { CommonModule } from '@angular/common';
import { unzipSync, strFromU8 } from 'fflate';
import { GSCDataSet, ParsedData } from '@taptimize/shared';

@Component({
  selector: 'app-upload',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './upload.html',
  styleUrl: './upload.css'
})
export class UploadComponent {
  isProcessing = false;
  errorMessage = '';
  successMessage = '';

  // File size limit: 50MB
  private readonly MAX_FILE_SIZE = 50 * 1024 * 1024;

  constructor(
    private parser: CsvParserService,
    private store: DataStoreService
  ) {}

  async onZipSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    
    if (!file) return;

    // Clear previous messages
    this.clearMessages();

    // Validate file
    if (!this.validateFile(file)) {
      return;
    }

    this.isProcessing = true;

    try {
      await this.processZipFile(file);
    } catch (error) {
      this.handleError('Failed to process ZIP file', error);
    } finally {
      this.isProcessing = false;
      // Clear the input so the same file can be selected again
      input.value = '';
    }
  }

  private validateFile(file: File): boolean {
    // Check file type
    if (!file.name.toLowerCase().endsWith('.zip')) {
      this.errorMessage = 'Please select a ZIP file.';
      return false;
    }

    // Check file size
    if (file.size > this.MAX_FILE_SIZE) {
      this.errorMessage = `File size must be less than ${this.MAX_FILE_SIZE / (1024 * 1024)}MB.`;
      return false;
    }

    // Check if file is empty
    if (file.size === 0) {
      this.errorMessage = 'The selected file is empty.';
      return false;
    }

    return true;
  }

  private async processZipFile(file: File) {
    try {
      // Read the file
      const arrayBuffer = await file.arrayBuffer();
      
      // Unzip the file
      let zipContents;
      try {
        zipContents = unzipSync(new Uint8Array(arrayBuffer));
      } catch (error) {
        throw new Error('Invalid or corrupted ZIP file.');
      }

      // Find all CSV files from Google Search Console
      const allCsvEntries = Object.entries(zipContents).filter(([filename]) =>
        filename.toLowerCase().endsWith('.csv')
      );

      if (allCsvEntries.length === 0) {
        throw new Error('No CSV files found in the ZIP archive.');
      }

      // Process ALL CSV files to create complete GSC dataset
      const datasets: GSCDataSet[] = [];
      
      for (const [filename, fileData] of allCsvEntries) {
        try {
          // Convert to text
          const text = strFromU8(fileData);
          
          // Parse CSV
          const parsed = await this.parser.parseCSVFromString(text);
          
          if (!parsed || parsed.length === 0) {
            continue;
          }

          // Determine file type and create dataset
          const dataset = this.createDataset(filename, parsed);
          if (dataset) {
            datasets.push(dataset);
          }
          
        } catch (error) {
          // Continue processing other files
        }
      }

      if (datasets.length === 0) {
        throw new Error('No valid Google Search Console data found in any CSV files.');
      }

      // Sort datasets by priority (Dates first, then others)
      datasets.sort((a, b) => {
        const priority = { dates: 0, queries: 1, pages: 2, countries: 3, devices: 4, search_appearance: 5 };
        return (priority[a.type] || 99) - (priority[b.type] || 99);
      });
      
      // Store all datasets
      this.store.setData(datasets, file.name);
      
      this.successMessage = `Successfully processed ${datasets.length} datasets with ${datasets.reduce((sum, d) => sum + d.rowCount, 0)} total rows`;
      
    } catch (error) {
      throw error;
    }
  }

  private handleError(message: string, error: any) {
    console.error(message, error);
    this.errorMessage = error instanceof Error ? error.message : message;
  }

  private clearMessages() {
    this.errorMessage = '';
    this.successMessage = '';
  }

  private createDataset(filename: string, data: any[]): GSCDataSet | null {
    const lowerFilename = filename.toLowerCase();
    
    // Determine dataset type based on filename
    let type: GSCDataSet['type'];
    let displayName: string;
    
    if (lowerFilename.includes('dates')) {
      type = 'dates';
      displayName = 'DATES';
    } else if (lowerFilename.includes('queries') || lowerFilename.includes('query')) {
      type = 'queries';
      displayName = 'QUERIES';
    } else if (lowerFilename.includes('pages') || lowerFilename.includes('page')) {
      type = 'pages';
      displayName = 'PAGES';
    } else if (lowerFilename.includes('countries') || lowerFilename.includes('country')) {
      type = 'countries';
      displayName = 'COUNTRIES';
    } else if (lowerFilename.includes('devices') || lowerFilename.includes('device')) {
      type = 'devices';
      displayName = 'DEVICES';
    } else if (lowerFilename.includes('search') && lowerFilename.includes('appearance')) {
      type = 'search_appearance';
      displayName = 'SEARCH APPEARANCE';
    } else {
      // Skip unknown file types
      return null;
    }

    // Basic validation - ensure we have the core metrics
    const firstRow = data[0];
    if (!firstRow) return null;
    
    const headers = Object.keys(firstRow).map(h => h.toLowerCase());
    const hasClicks = headers.some(h => h.includes('clicks'));
    const hasImpressions = headers.some(h => h.includes('impressions'));
    
    if (!hasClicks || !hasImpressions) {
      return null;
    }

    return {
      data,
      filename,
      type,
      displayName,
      rowCount: data.length
    };
  }
}